import React, { useState, useEffect } from 'react';
import BubbleInput from 'ps-components/BubbleInput/BubbleInput';
import { FormGroup, Input, TextArea } from 'ps-design';
import Select from 'ps-components/ReactSelect/ReactSelect';
import { string, func, object, array, bool } from 'prop-types';
import classnames from 'classnames';

import './ModelsInfoCard.scss';

const groupValues = [
  {
    value: 'personal',
    label: 'My models'
  },
  {
    value: 'site',
    label: 'Site'
  },
  {
    value: 'group',
    label: 'Group'
  }
];

const namePositionValues = [
  {
    value: 'before',
    label: 'Before'
  },
  {
    value: 'after',
    label: 'After'
  }
];

ModelsInfoCard.propTypes = {
  name: string,
  setName: func,
  scopeValue: string,
  setScopeValue: func,
  description: string,
  setDescription: func,
  namePosition: string,
  setNamePosition: func,
  slideout: object,
  groups: array,
  outputText: string,
  fillerText: string,
  labelText: string,
  allGroups: bool,
  setAllGroups: func,
  groupList: array,
  setGroupList: func,
  onGroupFocusChange: func,
  scrollIntoView: bool,
  permissions: object
};

export default function ModelsInfoCard(props) {
  const [showDescription, setShowDescription] = useState(false);
  // For bubble input focus for groups
  const [focused, setFocused] = useState(false);
  const {
    name,
    setName,
    scopeValue,
    setScopeValue,
    description,
    setDescription,
    namePosition,
    setNamePosition,
    slideout,
    groups,
    outputText,
    fillerText,
    labelText,
    allGroups,
    setAllGroups,
    groupList,
    setGroupList,
    onGroupFocusChange,
    scrollIntoView,
    permissions
  } = props;

  const filteredScopes = groupValues.filter(group => {
    if (
      (!permissions.group && group.value === 'group') ||
      (!permissions.site && group.value === 'site') ||
      (!permissions.personal && group.value === 'personal')
    )
      return false;
    else return true;
  });

  useEffect(() => {
    if (!slideout.open) setShowDescription(false);
  }, [slideout.open]);

  function handleToggleDescription() {
    if (showDescription) setDescription('');
    setShowDescription(!showDescription);
  }

  function handleNameChange(id, value) {
    setName(value);
  }

  function handleScopeChange(value) {
    setScopeValue(value.value);
  }

  function handleNamePositionChange(value) {
    setNamePosition(value.value);
  }

  function handleDescriptionChange(id, value) {
    setDescription(value);
  }

  function handleFocus() {
    setFocused(true);
  }

  function handleBlur() {
    setFocused(false);
  }

  function setAllGroupsFalse() {
    setAllGroups(false);
    if (onGroupFocusChange) onGroupFocusChange(true);
  }

  function setAllGroupsTrue() {
    setAllGroups(true);
    if (onGroupFocusChange) onGroupFocusChange(false);
  }

  const dropdownOptions = groups
    .filter(group => !groupList.find(currentGroups => currentGroups.value === group.id))
    .map(group => {
      return { label: group.name, value: group.id };
    });

  return (
    <>
      <div className="structure-models-info__general-container">
        <ListEntry
          className="structure-models-info__name-input"
          titleClassName="structure-models-info__name-input-title"
          listTitle="Bulk name models"
        >
          <span className="structure-models-info__name-text">Add text</span>
          <Input
            autoComplete="off"
            id="structure-models-info__name-input"
            name="structure-models-info__name-input"
            onChange={handleNameChange}
            placeholder={fillerText}
            value={name}
          />
          <Select
            id="structure-models-info__name-position-input"
            className="structure-models-info__name-position-input"
            options={namePositionValues}
            value={namePositionValues.find(option => option.value === namePosition)}
            onChange={handleNamePositionChange}
          />
          <span className="structure-models-info__name-text">{labelText}</span>
          <div className="structure-models-info__example-name-text">{outputText}</div>
        </ListEntry>
        <ListEntry
          className="pxl-options structure-models-info__group-select-container"
          titleClassName="structure-models-info__group-select-container-title"
          listTitle="Publish to"
        >
          <Select
            id="structure-models-info__group-input"
            className="structure-models-info__group-input"
            options={filteredScopes}
            value={groupValues.find(option => option.value === scopeValue)}
            onChange={handleScopeChange}
          />
          {scopeValue === 'group' && groups.length > 1 && (
            <>
              <label className="pxl-option pxl-option--radio pxl-option--primary">
                <input
                  type="checkbox"
                  checked={!allGroups}
                  onChange={setAllGroupsFalse}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className="pxl-option__input"
                />
                <div className="pxl-option__text">
                  <span>
                    Select one or more groups
                    {!allGroups && <span className="label-subtext">type to search for a specific group</span>}
                  </span>
                  {!allGroups && (
                    <BubbleInput
                      className="structure-models-info__bubble-input"
                      bubbles={groupList}
                      setBubbles={setGroupList}
                      dropdownOptions={dropdownOptions}
                      onFocus={setAllGroupsFalse}
                      hideInputLabel={true}
                      passFocus={focused}
                      scrollIntoView={scrollIntoView}
                    />
                  )}
                </div>
              </label>
              <label className="pxl-option pxl-option--radio pxl-option--primary">
                <input type="checkbox" checked={allGroups} onChange={setAllGroupsTrue} className="pxl-option__input" />
                <div className="pxl-option__text">
                  <span>Select all {groups.length} groups</span>
                </div>
              </label>
            </>
          )}
          {scopeValue === 'group' && groups.length === 1 && (
            <div className="structure-models-info__one-group-text">
              Model(s) will be added to the group <b>{groups[0].name}</b>
            </div>
          )}
        </ListEntry>
        <ListEntry className="structure-models-info__description-container" listTitle="Description">
          {showDescription ? (
            <FormGroup text="Model Description">
              <a onClick={handleToggleDescription} className="remove-description">
                Remove
              </a>
              <TextArea value={description} onChange={handleDescriptionChange} rows={7} />
            </FormGroup>
          ) : (
            <a onClick={handleToggleDescription} title="Add an optional description to the copied models.">
              Add Description
            </a>
          )}
        </ListEntry>
      </div>
    </>
  );
}

ListEntry.propTypes = {
  listTitle: string,
  children: array,
  titleClassName: string,
  className: string
};

function ListEntry({ listTitle, children, className, titleClassName }) {
  return (
    <div className="list__entry-container">
      <div className={classnames(titleClassName, 'list__entry-title')}>{listTitle}</div>
      <div className={classnames(className, 'list__entry-content')}>{children}</div>
    </div>
  );
}
